(() => {
  if (customElements.get('vibe-catch-game')) return;

  const COLS = 7;
  const ROWS = 8;
  const PILE_SIZE = 2;
  const SLOTS = 7;
  /* Level 1 easy, level 2 medium (unlocks 15% off + free gift), level 3 super hard (upgrades to 25% off).
     tools = how many Undo / Shuffle / Hint the player gets for that level. */
  const LEVELS = [
    { types: 4, sets: 2, layers: 2, tools: 3 },
    { types: 8, sets: 2, layers: 4, tools: 3 },
    { types: 10, sets: 3, layers: 6, tools: 1, time: 300, trap: true }
  ];

  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  /* Pick products for a level so they are easy to tell apart:
     group them by color family (light / red-pink / purple-blue / other), then take
     turns picking from each group. Products from the same series (same first word,
     e.g. two "BloomLick" toys) are only put together when there is no other choice. */
  const hexToRgb = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const colorFamily = (hex) => {
    const c = hexToRgb(hex);
    if (!c) return 'other';
    const [r, g, b] = c;
    if (r + g + b > 600) return 'light';
    if (b > r * 0.8 && b > g) return 'purple';
    if (r > g * 1.6 && r > b * 1.3) return 'red';
    if (g > r && g > b) return 'green';
    return 'other';
  };
  const seriesOf = (piece) => String((piece && piece.name) || '').trim().split(/\s+/)[0].toLowerCase();
  const pickDistinct = (pieces, count) => {
    const groups = {};
    pieces.forEach((p, i) => {
      if (!p || !p.img || p.ready === false) return;
      const fam = colorFamily(p.color);
      (groups[fam] = groups[fam] || []).push(i);
    });
    const lists = shuffle(Object.values(groups).map((list) => shuffle(list)));
    const picked = [];
    const series = new Set();
    let guard = 0;
    while (picked.length < count && lists.some((l) => l.length) && guard++ < 500) {
      lists.forEach((list) => {
        if (picked.length >= count || !list.length) return;
        let k = list.findIndex((i) => !series.has(seriesOf(pieces[i])));
        if (k < 0) k = 0;
        const id = list.splice(k, 1)[0];
        picked.push(id);
        series.add(seriesOf(pieces[id]));
      });
    }
    return picked;
  };

  /* Game sounds, synthesized with Web Audio (no audio files to download). Phones only allow sound
     after a tap, so the audio starts on the player's first touch. Muting also stops vibration,
     and the choice is remembered. */
  const Sfx = {
    ctx: null,
    master: null,
    muted: (() => {
      try {
        return localStorage.getItem('vc-muted') === '1';
      } catch (err) {
        return false;
      }
    })(),
    unlock() {
      if (this.muted) return;
      try {
        /* iPhone: plain game sound effects ("ambient"): they mix with the player's music, don't show in the Dynamic Island, and follow the ring/silent switch. */
        if (navigator.audioSession && navigator.audioSession.type !== 'ambient') {
          navigator.audioSession.type = 'ambient';
        }
        if (!this.ctx) {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (!AC) return;
          this.ctx = new AC();
          this.master = this.ctx.createGain();
          this.master.gain.value = 0.7;
          this.master.connect(this.ctx.destination);
        }
        if (this.ctx.state !== 'running') this.ctx.resume();
        if (!this.primed) {
          /* iPhone only opens Web Audio after a sound starts inside a tap: play one silent sample. */
          const buffer = this.ctx.createBuffer(1, 1, 22050);
          const src = this.ctx.createBufferSource();
          src.buffer = buffer;
          src.connect(this.ctx.destination);
          src.start(0);
          this.primed = true;
        }
      } catch (err) {}
    },
    setMuted(muted) {
      this.muted = !!muted;
      try {
        localStorage.setItem('vc-muted', this.muted ? '1' : '0');
      } catch (err) {}
      if (!this.muted) this.unlock();
    },
    tone(freq, start, dur, opts) {
      const o = opts || {};
      const c = this.ctx;
      const t = c.currentTime + start;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = o.type || 'sine';
      osc.frequency.setValueAtTime(freq, t);
      if (o.slide) osc.frequency.exponentialRampToValueAtTime(Math.max(40, freq * o.slide), t + dur);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(o.vol || 0.12, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(gain);
      gain.connect(this.master);
      osc.start(t);
      osc.stop(t + dur + 0.03);
    },
    buzz(pattern) {
      if (this.muted) return;
      try {
        if (navigator.vibrate) navigator.vibrate(pattern);
      } catch (err) {}
    },
    play(name) {
      if (this.muted) return;
      this.unlock();
      if (!this.ctx || this.ctx.state === 'closed') return;
      try {
        const tone = this.tone.bind(this);
        if (name === 'tap') {
          tone(560 + Math.random() * 80, 0, 0.09, { slide: 1.7, vol: 0.11 });
        } else if (name === 'blocked') {
          tone(200, 0, 0.1, { type: 'triangle', slide: 0.75, vol: 0.07 });
        } else if (name === 'match') {
          [784, 1047, 1319].forEach((f, i) => tone(f, i * 0.055, 0.18, { type: 'triangle', vol: 0.1 }));
          tone(2093, 0.16, 0.22, { vol: 0.04 });
          this.buzz(18);
        } else if (name === 'warn') {
          tone(466, 0, 0.08, { type: 'square', vol: 0.035 });
          tone(466, 0.13, 0.08, { type: 'square', vol: 0.035 });
        } else if (name === 'tool') {
          tone(330, 0, 0.16, { slide: 2.2, vol: 0.07 });
        } else if (name === 'clear') {
          [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 0.09, 0.24, { type: 'triangle', vol: 0.1 }));
          this.buzz([25, 40, 25]);
        } else if (name === 'win') {
          [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, i * 0.09, 0.28, { type: 'triangle', vol: 0.1 }));
          [1568, 2093].forEach((f, i) => tone(f, 0.5 + i * 0.07, 0.3, { vol: 0.05 }));
          this.buzz([30, 50, 30, 50, 60]);
        } else if (name === 'reward') {
          [880, 1175, 1760].forEach((f, i) => tone(f, i * 0.07, 0.22, { vol: 0.08 }));
        } else if (name === 'lose') {
          tone(392, 0, 0.32, { type: 'triangle', slide: 0.8, vol: 0.09 });
          tone(311, 0.26, 0.45, { type: 'triangle', slide: 0.75, vol: 0.09 });
          this.buzz(70);
        } else if (name === 'revive') {
          [659, 880, 1319].forEach((f, i) => tone(f, i * 0.06, 0.2, { vol: 0.09 }));
        }
      } catch (err) {}
    }
  };

  class VibeCatchGame extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const q = (sel) => this.querySelector(sel);
      this.boardEl = q('[data-vc-board]');
      this.slotEl = q('[data-vc-slots]');
      this.levelEl = q('[data-vc-level]');
      this.leftEl = q('[data-vc-left]');
      this.progressEl = q('[data-vc-progress]');
      this.toastEl = q('[data-vc-toast]');
      this.liveEl = q('[data-vc-live]');
      this.screens = {
        start: q('[data-vc-start]'),
        paused: q('[data-vc-paused]'),
        clear: q('[data-vc-clear]'),
        lose: q('[data-vc-lose]'),
        win: q('[data-vc-win]'),
        final: q('[data-vc-final]')
      };
      this.tools = {
        undo: q('[data-vc-tool="undo"]'),
        shuffle: q('[data-vc-tool="shuffle"]'),
        hint: q('[data-vc-tool="hint"]')
      };
      this.names = (this.dataset.names || '').split('|');
      this.pieces = [];
      try {
        const piecesEl = q('[data-vc-pieces]');
        const parsed = piecesEl ? JSON.parse(piecesEl.textContent) : [];
        this.pieces = Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        this.pieces = [];
      }
      this.pieces.forEach((piece, i) => {
        if (!piece) return;
        if (piece.name) this.names[i] = piece.name;
        /* Start downloading 3D models immediately (in parallel with the 3D engine), so the
           3D loader later finds them in the browser cache. Same request mode as the loader. */
        if (piece.model && this.getAttribute('data-vc-3d') === 'true' && window.fetch) {
          try {
            fetch(piece.model, { mode: 'cors', credentials: 'same-origin' }).catch(() => {});
          } catch (err) {}
        }
        if (piece.img) {
          const preload = new Image();
          preload.src = piece.img;
        }
      });
      this.pile = this.pieces.filter((piece) => piece && piece.img).length >= 1;
      if (this.pieces.some((piece) => piece && piece.img)) this.classList.add('vc-photos');
      if (this.pile) this.classList.add('vc-pile');
      this.cheers = (this.dataset.cheers || 'Nice!').split('|').filter(Boolean);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      document.documentElement.classList.add('vc-in-game');
      this.toolCounts = { undo: 3, shuffle: 3, hint: 3 };

      this.addEventListener('click', (e) => this.onClick(e));
      this.timerId = setInterval(() => this.tickTimer(), 250);
      if (window.PointerEvent) {
        this.boardEl.addEventListener('pointerdown', (e) => this.onBoardPointer(e));
      } else {
        this.boardEl.addEventListener(
          'touchstart',
          (e) => {
            const t = e.changedTouches[0];
            e.preventDefault();
            this.tapAt(t.clientX, t.clientY, 'touch');
          },
          { passive: false }
        );
      }
      if (/vcdebug/.test(window.location.search)) {
        this.debugEl = document.createElement('pre');
        this.debugEl.style.cssText =
          'position:fixed;left:8px;bottom:8px;z-index:9999;max-width:90vw;max-height:40vh;overflow:auto;margin:0;padding:8px;background:#000c;color:#0f0;font:11px/1.4 monospace;white-space:pre-wrap;pointer-events:none';
        document.body.appendChild(this.debugEl);
        window.addEventListener('error', (ev) => this.log('ERROR ' + ev.message));
        window.addEventListener('unhandledrejection', (ev) => this.log('REJECT ' + (ev.reason && ev.reason.message)));
        this.log('debug on');
      }

      this.buildSlots();
      this.level = 0;
      this.tray = [];
      this.history = [];
      this.levels = LEVELS;
      this.generate(LEVELS[0]);
      this.renderBoard();
      this.renderSlots();
      this.updateHud();
      this.renderBest();
      this.show('start');
      this.setupSound();
      this.finishPendingClaim();
      this.setupWarmup();
      if (this.getAttribute('data-vc-3d') === 'true') {
        setTimeout(() => {
          if (!this.classList.contains('vc-3d')) this.classList.add('vc-3d-off');
        }, 45000);
      }
    }

    setupSound() {
      const unlock = () => Sfx.unlock();
      this.addEventListener('pointerdown', unlock, { passive: true });
      this.addEventListener('touchend', unlock, { passive: true });
      this.addEventListener('click', unlock);
      /* iPhone pauses audio when the page is in the background; wake it on return. */
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && Sfx.ctx && Sfx.ctx.state !== 'running' && !Sfx.muted) {
          try {
            Sfx.ctx.resume();
          } catch (err) {}
        }
      });
      this.soundBtn = this.querySelector('[data-vc-sound]');
      this.renderSound();
    }

    renderSound() {
      const btn = this.soundBtn;
      if (!btn) return;
      btn.setAttribute('aria-pressed', Sfx.muted ? 'false' : 'true');
      btn.classList.toggle('is-muted', Sfx.muted);
      btn.setAttribute('aria-label', Sfx.muted ? btn.dataset.labelOff || 'Sound off' : btn.dataset.labelOn || 'Sound on');
    }

    onClick(e) {
      const soundBtn = e.target.closest('[data-vc-sound]');
      if (soundBtn && this.contains(soundBtn)) {
        Sfx.setMuted(!Sfx.muted);
        this.renderSound();
        Sfx.play('tap');
        return;
      }
      const shareLink = e.target.closest('[data-vc-share-link]');
      if (shareLink && this.contains(shareLink)) return this.onShareLink(shareLink, e);
      const shopLink = e.target.closest('[data-vc-shop]');
      if (shopLink && this.contains(shopLink)) {
        this.track('shop_click', { code: shopLink.dataset.code || '' });
        return;
      }
      const target = e.target.closest('button');
      if (target && this.contains(target) && !target.matches('.vc-item')) {
        if (target.hasAttribute('data-vc-play')) return this.newGame();
        if (target.hasAttribute('data-vc-next')) return this.startLevel(this.level + 1, true);
        if (target.hasAttribute('data-vc-retry')) return this.startLevel(this.level, false);
        if (target.hasAttribute('data-vc-keep-claim')) return this.show('win');
        if (target.hasAttribute('data-vc-revive')) return this.shareToRevive(target);
        if (target.hasAttribute('data-vc-menu')) return this.pause();
        if (target.hasAttribute('data-vc-resume')) return this.resume();
        if (target.hasAttribute('data-vc-copy')) return this.copyCode(target);
        if (target.dataset.vcTool) return this.useTool(target.dataset.vcTool);
        return;
      }
      if (!this.boardEl.contains(e.target)) return;
      if (e.detail === 0 && target) {
        this.log(`key ${target.dataset.id}`);
        return this.pick(+target.dataset.id);
      }
      if (Date.now() - (this.lastPointer || 0) < 1500) return;
      this.tapAt(e.clientX, e.clientY, 'click');
    }

    onBoardPointer(e) {
      if (e.button > 0) return;
      e.preventDefault();
      this.lastPointer = Date.now();
      this.tapAt(e.clientX, e.clientY, e.type);
    }

    tapAt(clientX, clientY, source) {
      const now = Date.now();
      if (now - (this.lastTap || 0) < 350) return;
      const rect = this.boardEl.getBoundingClientRect();
      if (!rect.width || !rect.height) return this.log(`${source}: board has no size`);
      const ux = ((clientX - rect.left) / rect.width) * COLS;
      const uy = ((clientY - rect.top) / rect.height) * ROWS;
      let hit = null;
      this.items.forEach((item) => {
        if (item.state !== 'board') return;
        if (this.pile) {
          const dist = Math.hypot(ux - (item.x + item.size / 2), uy - (item.y + item.size / 2));
          if (dist > item.size * 0.4) return;
        } else if (ux < item.x || ux >= item.x + 1 || uy < item.y || uy >= item.y + 1) return;
        if (!hit || item.z > hit.z) hit = item;
      });
      this.log(`${source} @${ux.toFixed(1)},${uy.toFixed(1)} hit=${hit ? hit.id : '-'} covered=${hit ? this.isCovered(hit) : '-'} running=${this.running} busy=${this.busy}`);
      if (!hit) return;
      this.lastTap = now;
      if (!this.pile && this.isCovered(hit)) {
        const el = this.itemEls[hit.id];
        if (el) {
          el.classList.remove('is-locked');
          void el.offsetWidth;
          el.classList.add('is-locked');
        }
        return;
      }
      this.pick(hit.id);
    }

    log(msg) {
      if (!this.debugEl) return;
      this.debugEl.textContent = `${msg}\n${this.debugEl.textContent}`.slice(0, 600);
    }

    wait(ms) {
      return new Promise((r) => setTimeout(r, this.reduceMotion ? 0 : ms));
    }

    /* Personal best: highest level cleared, kept only in this browser. */
    readBest() {
      try {
        return Math.max(0, parseInt(localStorage.getItem('vc-best') || '0', 10) || 0);
      } catch (err) {
        return 0;
      }
    }

    saveBest(cleared) {
      if (cleared > this.readBest()) {
        try {
          localStorage.setItem('vc-best', String(cleared));
        } catch (err) {}
      }
      this.renderBest();
    }

    renderBest() {
      const el = this.querySelector('[data-vc-best]');
      if (!el) return;
      const best = Math.min(this.readBest(), LEVELS.length);
      el.hidden = best < 1;
      if (best < 1) return;
      const label = el.dataset.label || 'Best';
      el.textContent = best >= LEVELS.length ? el.dataset.all || 'All levels cleared!' : `${label}: Level ${best}/${LEVELS.length}`;
    }

    /* Put one of the shop's real products in the pop-up badge. */
    setBadge(screen) {
      const badge = screen.querySelector('.vc-card__badge');
      if (!badge) return;
      const withImage = this.pieces.map((p, i) => (p && p.img ? i : -1)).filter((i) => i >= 0);
      if (!withImage.length) return;
      const type = withImage[Math.floor(Math.random() * withImage.length)];
      badge.innerHTML = this.icon(type);
    }

    /* Rewards (15% after level 2, 25% after level 3): email first, then reveal the code.
       One email unlocks every reward box; remembered in this browser. */
    setupReward() {
      this.querySelectorAll('[data-vc-reward]').forEach((box) => {
        if (box.dataset.ready) return;
        box.dataset.ready = '1';
        const form = box.querySelector('[data-vc-unlock-form]');
        if (!form) return;
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const input = form.querySelector('input[type="email"]');
          if (input && !input.checkValidity()) {
            input.reportValidity();
            return;
          }
          const btn = form.querySelector('button[type="submit"]');
          if (btn) btn.disabled = true;
          this.setClaimError(form, '');
          const fail = (msg) => {
            if (btn) btn.disabled = false;
            this.setClaimError(form, msg);
          };
          /* Only reveal the code once Shopify confirms the email was saved. A spam check (captcha)
             needs a real page submit, so hand over to the normal form and finish when they return. */
          const nativeSubmit = () => {
            try {
              localStorage.setItem('vc-claim-pending', JSON.stringify({ level: this.level || 0, at: Date.now() }));
            } catch (err) {}
            HTMLFormElement.prototype.submit.call(form);
          };
          fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' })
            .then((res) => {
              const url = res.url || '';
              if (/customer_posted=true/.test(url)) return this.claimSucceeded(box, true);
              if (/\/challenge/.test(url)) return nativeSubmit();
              if (!res.ok) return fail(this.dataset.claimRetry || "We couldn't save your email. Please try again.");
              return res.text().then((html) => {
                if (/customer_posted=true|form-success|posted_successfully/.test(html)) this.claimSucceeded(box, true);
                else fail(this.dataset.claimCheck || 'Please check your email address and try again.');
              });
            })
            .catch(() => fail(this.dataset.claimOffline || 'Connection problem. Please try again.'));
        });
      });
    }

    claimSucceeded(box, focus) {
      try {
        localStorage.setItem('vc-claimed', '1');
        localStorage.removeItem('vc-claim-pending');
      } catch (err) {}
      this.track('claim', { code: (box && box.dataset.code) || '' });
      Sfx.play('reward');
      this.revealCode(focus, box);
      this.applyCode(box);
    }

    /* Apply the code to this shopper's cart in the background (same as opening the store's
       shareable discount link), so checkout picks it up with no typing. */
    applyCode(box) {
      const code = box && box.dataset.code;
      if (!code) return;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const mark = (ok) => {
        let note = box.querySelector('[data-vc-applied]');
        if (!note) {
          note = document.createElement('p');
          note.className = 'vc-code__applied';
          note.setAttribute('data-vc-applied', '');
          note.setAttribute('role', 'status');
          const chip = box.querySelector('[data-vc-copy]');
          if (chip) chip.insertAdjacentElement('afterend', note);
          else return;
        }
        note.textContent = ok
          ? this.dataset.appliedText || '✓ Applied! It will be taken off automatically at checkout.'
          : this.dataset.applyFailText || 'Tap the shop button to apply your code.';
        note.classList.toggle('is-ok', ok);
      };
      if (this.appliedCode === code) return mark(true);
      fetch(root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart.js'), {
        credentials: 'same-origin'
      })
        .then((res) => {
          if (!res.ok) throw new Error('apply ' + res.status);
          this.appliedCode = code;
          mark(true);
        })
        .catch(() => mark(false));
    }

    setClaimError(form, msg) {
      let el = form.querySelector('[data-vc-unlock-error]');
      if (!el && msg) {
        el = document.createElement('p');
        el.className = 'vc-unlock__error';
        el.setAttribute('data-vc-unlock-error', '');
        el.setAttribute('role', 'alert');
        form.appendChild(el);
      }
      if (!el) return;
      el.textContent = msg;
      el.hidden = !msg;
    }

    /* Back from a captcha page submit: finish the claim and show the reward again. */
    finishPendingClaim() {
      let pending = null;
      try {
        pending = JSON.parse(localStorage.getItem('vc-claim-pending') || 'null');
      } catch (err) {}
      if (!pending || Date.now() - pending.at > 30 * 60000) return false;
      const posted = /customer_posted=true/.test(window.location.search);
      this.level = Math.min(Math.max(pending.level || 0, 0), LEVELS.length - 1);
      const screen = this.level >= LEVELS.length - 1 ? 'final' : 'win';
      this.show(screen);
      const box = this.screens[screen] && this.screens[screen].querySelector('[data-vc-reward]');
      if (posted) {
        this.claimSucceeded(box, true);
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch (err) {}
      } else {
        try {
          localStorage.removeItem('vc-claim-pending');
        } catch (err) {}
        const form = box && box.querySelector('[data-vc-unlock-form]');
        if (form) this.setClaimError(form, this.dataset.claimCheck || 'Please check your email address and try again.');
      }
      return true;
    }

    /* Ad tracking: sends game milestones to Shopify customer events, so a custom pixel can
       forward them to Meta (e.g. how many players clear level 3). Never breaks the game. */
    track(name, data) {
      try {
        const payload = Object.assign({ level: (this.level || 0) + 1 }, data || {});
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('vibe_catch_' + name, payload);
        }
      } catch (err) {}
    }

    isClaimed() {
      try {
        return localStorage.getItem('vc-claimed') === '1';
      } catch (err) {
        return false;
      }
    }

    revealCode(focus, focusBox) {
      this.querySelectorAll('[data-vc-reward]').forEach((box) => {
        const unlock = box.querySelector('[data-vc-unlock]');
        const code = box.querySelector('[data-vc-code]');
        if (unlock) unlock.hidden = true;
        if (code) code.hidden = false;
      });
      if (focus && focusBox) {
        const chip = focusBox.querySelector('[data-vc-copy]');
        if (chip) chip.focus({ preventScroll: true });
      }
    }

    copyCode(btn) {
      const box = btn.closest('[data-vc-reward]');
      const code = box ? box.dataset.code : '';
      const label = btn.querySelector('[data-vc-copy-text]');
      const flash = () => {
        if (!label) return;
        if (!label.dataset.orig) label.dataset.orig = label.textContent;
        label.textContent = btn.dataset.copied || 'Copied!';
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => (label.textContent = label.dataset.orig), 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(flash, () => this.copyFallback(code, flash));
      } else {
        this.copyFallback(code, flash);
      }
    }

    copyFallback(code, flash) {
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        flash();
      } catch (err) {}
      ta.remove();
    }

    show(name) {
      Object.entries(this.screens).forEach(([key, el]) => {
        if (el) el.hidden = key !== name;
      });
      if (name === 'win' || name === 'final') {
        this.setupReward();
        if (this.isClaimed()) {
          this.revealCode(false);
          /* Apply the code of the reward on screen (25% replaces 15% after level 3). */
          const box = this.screens[name] && this.screens[name].querySelector('[data-vc-reward]');
          if (box) this.applyCode(box);
        }
      }
      if (name === 'lose') {
        const reviveBtn = this.querySelector('[data-vc-revive]');
        const reviveBox = this.querySelector('[data-vc-revive-box]');
        const links = this.querySelector('[data-vc-share-links]');
        if (reviveBox) reviveBox.hidden = !!this.revived;
        else if (reviveBtn) reviveBtn.hidden = !!this.revived;
        if (links) links.hidden = true;
        /* Losing level 3 never takes away the level 2 reward: show it with a shop button. */
        const keep = this.querySelector('[data-vc-keep]');
        const onLast = this.level >= LEVELS.length - 1;
        if (keep) {
          keep.hidden = !onLast;
          const claimed = this.isClaimed();
          const keepShop = keep.querySelector('[data-vc-shop]');
          const keepClaim = keep.querySelector('[data-vc-keep-claim]');
          if (keepShop) keepShop.hidden = !claimed;
          if (keepClaim) keepClaim.hidden = claimed;
        }
        const loseShop = this.querySelector('[data-vc-lose-shop]');
        if (loseShop) loseShop.hidden = onLast && !!keep;
        const note = this.querySelector('.vc-revive__note');
        if (note && note.dataset.orig) {
          note.textContent = note.dataset.orig;
          note.classList.remove('is-warn');
        }
        if (this.awayWatch) {
          document.removeEventListener('visibilitychange', this.awayWatch);
          window.removeEventListener('blur', this.awayWatch);
          window.removeEventListener('focus', this.awayWatch);
          this.awayWatch = null;
        }
        /* Different title when the timer ran out. */
        const card = this.screens.lose;
        const title = card && card.querySelector('.vc-card__title');
        const textEl = card && card.querySelector('.vc-card__text');
        [title, textEl].forEach((el) => {
          if (el && !el.dataset.orig) el.dataset.orig = el.textContent;
        });
        const timeUp = this.loseReason === 'time';
        if (title) title.textContent = timeUp ? this.dataset.timeUpTitle || "Time's up!" : title.dataset.orig;
        /* Level 2 is where most players drop: remind them the reward is one level away. */
        const rewardLevel = this.level === LEVELS.length - 2;
        const loseText = rewardLevel
          ? this.dataset.loseRewardText || 'So close! Your 15% OFF + Free Gift Set is just one level away.'
          : textEl && textEl.dataset.orig;
        if (textEl) textEl.textContent = timeUp ? this.dataset.timeUpText || 'So close. Share to get +60 seconds.' : loseText;
      }
      if (name && this.screens[name]) {
        this.setBadge(this.screens[name]);
        const btn = this.screens[name].querySelector('.vc-play') || this.screens[name].querySelector('[data-vc-unlock]:not([hidden]) input[type="email"], [data-vc-code]:not([hidden]) [data-vc-copy]') || this.screens[name].querySelector('button, a');
        if (btn) btn.focus({ preventScroll: true });
      }
    }

    pause() {
      if (!this.running) return;
      this.running = false;
      this.updateHud();
      this.show('paused');
    }

    resume() {
      this.running = true;
      this.updateHud();
      this.show(null);
    }

    newGame() {
      document.documentElement.classList.add('vc-in-game');
      this.toolCounts = { undo: 3, shuffle: 3, hint: 3 };
      /* Phones often reload the page while the player is away sharing. Pick up at the level they
         lost on (within 30 minutes) instead of sending them back to level 1. */
      const resume = this.readResume();
      if (resume) {
        this.startLevel(resume.level, false);
        if (resume.sharing && !resume.revived) {
          this.revived = true;
          this.toast(this.dataset.resumeToast || 'Welcome back! You are revived. Keep going!');
        } else {
          this.revived = !!resume.revived;
        }
        return;
      }
      this.startLevel(0, false);
    }

    /* Remember the level the player lost on, and whether they went to share, in this browser. */
    saveResume(sharing) {
      try {
        localStorage.setItem('vc-resume', JSON.stringify({ level: this.level, revived: !!this.revived, sharing: !!sharing, at: Date.now() }));
      } catch (err) {}
    }

    readResume() {
      try {
        const r = JSON.parse(localStorage.getItem('vc-resume') || 'null');
        localStorage.removeItem('vc-resume');
        if (r && r.level > 0 && r.level < LEVELS.length && Date.now() - r.at < 30 * 60000) return r;
      } catch (err) {}
      return null;
    }

    clearResume() {
      try {
        localStorage.removeItem('vc-resume');
      } catch (err) {}
    }

    /* Loading warm-up on the title screen: a row of slots (like the game tray) that fills with
       each product's photo the moment its 3D model is ready. Tap a filled card to see its name.
       Stops by itself once the game is ready. */
    setupWarmup() {
      const slotsEl = this.querySelector('[data-vc-warmup-slots]');
      const statusEl = this.querySelector('[data-vc-warmup-status]');
      const tipEl = this.querySelector('[data-vc-warmup-tip]');
      const nameEl = this.querySelector('[data-vc-warmup-name]');
      if (!slotsEl || this.getAttribute('data-vc-3d') !== 'true') return;
      const withImg = this.pieces.filter((p) => p && p.img);
      const n = Math.max(1, Math.min(LEVELS[0].types, withImg.length));
      slotsEl.style.setProperty('--n', n);
      const slots = [];
      for (let k = 0; k < n; k++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'vc-wslot' + (k === 0 ? ' is-next' : '');
        b.disabled = true;
        slotsEl.appendChild(b);
        slots.push(b);
      }
      const tips = [
        'Tap a toy to move it into your tray.',
        'Collect 3 of the same toy to clear them.',
        'You have 7 slots. Don’t let them fill up!',
        'Stuck? Try Undo, Shuffle or Hint.',
      ];
      let tipIndex = 0;
      const showTip = () => {
        if (!tipEl) return;
        tipEl.classList.add('is-out');
        setTimeout(() => {
          tipEl.textContent = tips[tipIndex % tips.length];
          tipEl.classList.remove('is-out');
          tipIndex += 1;
        }, 300);
      };
      showTip();
      let nameTimer = null;
      const showName = (text) => {
        if (!nameEl) return;
        nameEl.textContent = text;
        nameEl.classList.add('is-on');
        clearTimeout(nameTimer);
        nameTimer = setTimeout(() => nameEl.classList.remove('is-on'), 1600);
      };
      const shown = new Set();
      let filled = 0;
      const loading = () => !this.classList.contains('vc-3d') && !this.classList.contains('vc-3d-off');
      const tick = () => {
        if (!loading()) {
          clearInterval(timer);
          clearInterval(tipTimer);
          return;
        }
        withImg.forEach((p) => {
          if (p.ready !== true || shown.has(p) || filled >= n) return;
          shown.add(p);
          const slot = slots[filled];
          filled += 1;
          const img = document.createElement('img');
          img.src = p.img;
          img.alt = '';
          slot.appendChild(img);
          slot.disabled = false;
          slot.setAttribute('aria-label', p.name || '');
          slot.classList.remove('is-next');
          slot.classList.add('is-filled');
          if (slots[filled]) slots[filled].classList.add('is-next');
          slot.addEventListener('click', () => {
            slot.classList.remove('is-wiggle');
            void slot.offsetWidth;
            slot.classList.add('is-wiggle');
            showName(p.name || '');
          });
        });
        if (statusEl) statusEl.textContent = filled >= n ? 'Ready!' : `Unpacking your toys… ${filled}/${n}`;
      };
      const timer = setInterval(tick, 200);
      const tipTimer = setInterval(showTip, 3200);
      tick();
    }

    /* Rebuild the preview pile on the title screen (used when 3D products finish loading). */
    regenerate() {
      if (!this.screens || !this.screens.start || this.screens.start.hidden) return;
      this.generate(LEVELS[this.level || 0]);
    }

    startLevel(index, bonus) {
      this.clearResume();
      this.level = Math.min(index, LEVELS.length - 1);
      const toolsPerLevel = LEVELS[this.level].tools || 3;
      this.toolCounts = { undo: toolsPerLevel, shuffle: toolsPerLevel, hint: toolsPerLevel };
      this.revived = false;
      this.loseReason = '';
      this.track('level_start');
      this.timed = !!LEVELS[this.level].time;
      this.timeLeft = (LEVELS[this.level].time || 0) * 1000;
      this.lastTick = performance.now();
      this.renderTimer();
      this.classList.toggle('vc-hard', this.level === LEVELS.length - 1);
      this.tray = [];
      this.history = [];
      this.busy = false;
      this.inFlight = 0;
      this.clearing = 0;
      this.clearingIds = new Set();
      this.running = true;
      this.generate(LEVELS[this.level]);
      this.renderBoard();
      this.renderSlots();
      this.updateHud();
      this.show(null);
    }

    pileSpot() {
      const tri = () => (Math.random() + Math.random()) / 2;
      const size = PILE_SIZE * (0.85 + Math.random() * 0.3);
      const cx = size / 2 + tri() * (COLS - size);
      const cy = size / 2 + tri() * (ROWS - size);
      return { x: cx - size / 2, y: cy - size / 2, size, r: Math.round(Math.random() * 90 - 45) };
    }

    generatePile({ types, sets, trap }) {
      const available = this.pieces.filter((piece) => piece && piece.img && piece.ready !== false).length;
      const typeCount = Math.max(1, Math.min(types, available));
      const setCount = Math.ceil((types * sets) / typeCount);
      const total = typeCount * 3 * setCount;
      const typeIds = pickDistinct(this.pieces, typeCount);
      let pool = [];
      if (trap) {
        /* "Pair trap" (level 3): toys drop into the box in list order, so the first ones end up at
           the bottom. One toy from every set of three drops first and gets buried; the other two land
           on top, so the surface is full of pairs and players must dig for each third one. */
        const bottom = [];
        const top = [];
        typeIds.forEach((t) => {
          for (let s = 0; s < setCount; s++) {
            bottom.push(t);
            top.push(t, t);
          }
        });
        pool = shuffle(bottom).concat(shuffle(top));
      } else {
        typeIds.forEach((t) => {
          for (let k = 0; k < 3 * setCount; k++) pool.push(t);
        });
        shuffle(pool);
      }
      this.total = total;
      this.items = pool.map((type, i) => Object.assign({ id: i, type, z: i, state: 'board' }, this.pileSpot()));
    }

    generate({ types, sets, layers, trap }) {
      if (this.pile) return this.generatePile({ types, sets, trap });
      const total = types * 3 * sets;
      const pool = [];
      for (let t = 0; t < types; t++) for (let k = 0; k < 3 * sets; k++) pool.push(t);
      shuffle(pool);

      const cellsByLayer = [];
      for (let l = 0; l < layers; l++) {
        const off = l % 2 ? 0.5 : 0;
        const cells = [];
        for (let y = 0; y + off <= ROWS - 1; y++) {
          for (let x = 0; x + off <= COLS - 1; x++) cells.push({ x: x + off, y: y + off, z: l });
        }
        cellsByLayer.push(shuffle(cells));
      }

      const positions = [];
      const weights = cellsByLayer.map((_, l) => layers - l + 1);
      const weightSum = weights.reduce((a, b) => a + b, 0);
      cellsByLayer.forEach((cells, l) => {
        const quota = l === layers - 1 ? total - positions.length : Math.round((total * weights[l]) / weightSum);
        positions.push(...cells.slice(0, Math.max(0, Math.min(quota, cells.length))));
      });
      let guard = 0;
      while (positions.length < total && guard < 1000) {
        const layer = cellsByLayer[guard % layers];
        const cell = layer.find((c) => !positions.includes(c));
        if (cell) positions.push(cell);
        guard++;
      }

      this.total = total;
      this.items = positions.slice(0, total).map((p, i) => ({ id: i, type: pool[i], x: p.x, y: p.y, z: p.z, state: 'board' }));
    }

    isCovered(item) {
      if (this.pile) {
        const ax = item.x + item.size / 2;
        const ay = item.y + item.size / 2;
        return this.items.some((o) => {
          if (o.state !== 'board' || o.z <= item.z) return false;
          const dx = o.x + o.size / 2 - ax;
          const dy = o.y + o.size / 2 - ay;
          return Math.hypot(dx, dy) < Math.max(o.size, item.size) * 0.42;
        });
      }
      return this.items.some(
        (o) => o.state === 'board' && o.z > item.z && Math.abs(o.x - item.x) < 1 && Math.abs(o.y - item.y) < 1
      );
    }

    icon(type) {
      const piece = this.pieces && this.pieces[type];
      if (piece && piece.img) {
        const src = String(piece.img).replace(/"/g, '&quot;');
        return `<img src="${src}" alt="" draggable="false" decoding="async">`;
      }
      return `<svg aria-hidden="true" focusable="false"><use href="#vc-piece-${type}"></use></svg>`;
    }

    renderBoard() {
      this.boardEl.innerHTML = '';
      this.itemEls = {};
      this.items
        .filter((i) => i.state === 'board')
        .sort((a, b) => a.z - b.z)
        .forEach((item) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'vc-item';
          btn.dataset.id = item.id;
          if (this.pile) {
            btn.classList.add('vc-item--pile');
            btn.style.left = `${(item.x / COLS) * 100}%`;
            btn.style.top = `${(item.y / ROWS) * 100}%`;
            btn.style.width = `${(item.size / COLS) * 100}%`;
            btn.style.height = `${(item.size / ROWS) * 100}%`;
            btn.style.setProperty('--vc-rot', `${item.r}deg`);
          } else {
            btn.style.left = `${(item.x / COLS) * 100}%`;
            btn.style.top = `${(item.y / ROWS) * 100}%`;
          }
          btn.style.zIndex = item.z + 1;
          btn.innerHTML = `<span class="vc-item__tile">${this.icon(item.type)}</span>`;
          this.boardEl.appendChild(btn);
          this.itemEls[item.id] = btn;
        });
      this.refreshCover();
    }

    refreshCover() {
      this.items.forEach((item) => {
        const btn = this.itemEls[item.id];
        if (!btn || item.state !== 'board') return;
        const covered = this.isCovered(item);
        btn.classList.toggle('is-covered', covered);
        btn.setAttribute('aria-disabled', covered ? 'true' : 'false');
        btn.setAttribute('aria-label', this.names[item.type] || 'Item');
      });
    }

    buildSlots() {
      this.slotEl.innerHTML = '';
      this.slotCells = [];
      for (let i = 0; i < SLOTS; i++) {
        const cell = document.createElement('div');
        cell.className = 'vc-slot';
        this.slotEl.appendChild(cell);
        this.slotCells.push(cell);
      }
    }

    renderSlots(clearing = []) {
      this.slotCells.forEach((cell, i) => {
        const item = this.tray[i];
        /* A toy still flying keeps its slot reserved but empty until it lands. */
        const shown = item && !item.landing ? item : null;
        const key = shown ? `${shown.id}:${shown.type}` : "";
        if (cell.dataset.vcItem !== key) {
          cell.innerHTML = shown ? this.icon(shown.type) : '';
          cell.dataset.vcItem = key;
        }
        cell.classList.toggle('is-filled', !!shown);
        cell.classList.toggle('is-clearing', !!shown && (clearing.includes(shown.id) || (this.clearingIds && this.clearingIds.has(shown.id))));
      });
      const danger = this.tray.length >= SLOTS - 2;
      if (danger && !this.wasDanger && this.running) Sfx.play('warn');
      this.wasDanger = danger;
      this.slotEl.classList.toggle('is-danger', danger);
    }

    updateHud() {
      const left = this.items.filter((i) => i.state === 'board').length;
      const gone = this.items.filter((i) => i.state === 'gone').length;
      this.levelEl.textContent = `${this.level + 1}/${LEVELS.length}`;
      this.leftEl.textContent = left;
      this.progressEl.style.width = `${Math.round((gone / this.total) * 100)}%`;
      Object.entries(this.tools).forEach(([name, btn]) => {
        if (!btn) return;
        const count = this.toolCounts[name];
        btn.querySelector('[data-vc-count]').textContent = count;
        btn.disabled = !this.running || count <= 0;
      });
    }

    toast(custom) {
      if (!this.toastEl) return;
      const text = custom || this.cheers[Math.floor(Math.random() * this.cheers.length)];
      this.toastEl.textContent = text;
      this.toastEl.classList.remove('is-show');
      void this.toastEl.offsetWidth;
      this.toastEl.classList.add('is-show');
      if (this.liveEl) this.liveEl.textContent = text;
    }

    async fly(fromEl, toEl, type) {
      if (this.reduceMotion || !fromEl || !toEl) return;
      const a = fromEl.getBoundingClientRect();
      const b = toEl.getBoundingClientRect();
      const ghost = document.createElement('div');
      ghost.className = this.pile ? 'vc-ghost vc-ghost--pile' : 'vc-ghost';
      ghost.innerHTML = this.icon(type);
      Object.assign(ghost.style, { left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px` });
      document.body.appendChild(ghost);
      ghost.getBoundingClientRect();
      ghost.style.transform = `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(${b.width / a.width})`;
      await this.wait(220);
      ghost.remove();
    }

    insertIndex(type) {
      let idx = -1;
      this.tray.forEach((it, i) => {
        if (it.type === type) idx = i;
      });
      return idx === -1 ? this.tray.length : idx + 1;
    }

    /* Tapping doesn't wait for the previous toy to land: each toy reserves its slot right away
       and flies there in the background, so players can tap quickly one after another. */
    async pick(id) {
      if (!this.running) return;
      const item = this.items[id];
      if (!item || item.state !== 'board' || (!this.pile && this.isCovered(item))) return;
      /* Level 3 (3D): a toy with another toy lying on top of it can't be taken; flash the one on top. */
      if (this.strictCover() && this.isCovered(item)) {
        const top = this.coveredBy ? this.coveredBy(item) : null;
        if (top && this.onHint) this.onHint(top, 900);
        const now = performance.now();
        Sfx.play('blocked');
        if (!this.lastBlockToast || now - this.lastBlockToast > 1600) {
          this.lastBlockToast = now;
          this.toast(this.dataset.coveredToast || 'Grab the toy on top first!');
        }
        return;
      }
      if (this.tray.length >= SLOTS) return;
      Sfx.play('tap');
      this.inFlight = (this.inFlight || 0) + 1;

      const idx = this.insertIndex(item.type);
      const el = this.itemEls[id];
      item.state = 'slot';
      item.landing = true;
      this.tray.splice(idx, 0, item);
      this.history.push(item);
      if (el) el.style.visibility = 'hidden';
      this.refreshCover();
      this.renderSlots();
      this.updateHud();
      try {
        await this.fly(el, this.slotCells[Math.min(this.tray.indexOf(item), SLOTS - 1)], item.type, item);
      } catch (err) {
        this.log('fly error ' + err.message);
      }
      if (el) el.remove();
      delete this.itemEls[id];
      item.landing = false;
      this.inFlight -= 1;
      this.renderSlots();
      await this.matchLanded(item.type);
      this.updateHud();
      this.checkEnd();
    }

    /* Clear 3 landed toys of the same kind. */
    async matchLanded(type) {
      const same = this.tray.filter((s) => s.type === type && !s.landing && !(this.clearingIds && this.clearingIds.has(s.id)));
      if (same.length < 3) return;
      const ids = same.slice(0, 3).map((s) => s.id);
      this.clearingIds = this.clearingIds || new Set();
      ids.forEach((i) => this.clearingIds.add(i));
      this.clearing = (this.clearing || 0) + 1;
      this.renderSlots();
      this.toast();
      Sfx.play('match');
      await this.wait(260);
      this.tray = this.tray.filter((s) => !ids.includes(s.id));
      ids.forEach((i) => {
        this.items[i].state = 'gone';
        this.clearingIds.delete(i);
      });
      this.history = this.history.filter((h) => !ids.includes(h.id));
      this.clearing -= 1;
      this.renderSlots();
    }

    checkEnd() {
      if (this.inFlight || this.clearing) return;
      const left = this.items.some((i) => i.state === 'board') || this.tray.length;
      if (!left) {
        this.running = false;
        this.updateHud();
        this.saveBest(this.level + 1);
        /* Level 1 → next level, level 2 → 15% reward (with the level 3 challenge), level 3 → 25% reward. */
        const last = LEVELS.length - 1;
        this.track('level_clear', { revived: !!this.revived });
        Sfx.play(this.level >= last - 1 ? 'win' : 'clear');
        this.show(this.level >= last ? 'final' : this.level === last - 1 ? 'win' : 'clear');
        return;
      }
      if (this.tray.length >= SLOTS) {
        this.running = false;
        this.updateHud();
        this.track('level_fail', { reason: 'slots' });
        Sfx.play('lose');
        this.show('lose');
        this.saveResume(false);
      }
    }

    /* Share to revive: once per level. Opens the phone's share sheet (or share links on desktop);
       after sharing, the last 3 toys in the tray go back into the box and play continues. */
    shareToRevive(btn) {
      if (this.revived) return;
      this.saveResume(true);
      const url = this.dataset.shareUrl || location.origin + location.pathname;
      const text = this.dataset.shareText || 'Can you beat Vibe Catch? Win up to 25% off.';
      /* Computers: show the share buttons (Facebook, WhatsApp, X, Copy link); the system share sheet
         there closes without leaving the page, so a real share looked like a cancelled one. */
      const desktop = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (navigator.share && !desktop) {
        /* Some browsers report a cancelled share sheet as success, so a share only counts when the
           player left the page for another app, or spent at least 3 seconds in the share sheet. */
        const started = performance.now();
        let away = false;
        const onVis = () => {
          if (document.hidden) away = true;
        };
        document.addEventListener('visibilitychange', onVis);
        const finish = () => document.removeEventListener('visibilitychange', onVis);
        navigator
          .share({ title: document.title, text, url })
          .then(() => {
            finish();
            if (away || performance.now() - started >= 3000) this.revive();
            else this.shareNotDone();
          })
          .catch((err) => {
            finish();
            if (err && err.name === 'AbortError') this.shareNotDone();
            else this.showShareLinks();
          });
        return;
      }
      this.showShareLinks();
    }

    shareNotDone() {
      const note = this.querySelector('.vc-revive__note');
      if (!note) return;
      if (!note.dataset.orig) note.dataset.orig = note.textContent;
      note.textContent = this.dataset.shareNotDone || 'Share not sent. Pick an app and send it to a friend to revive.';
      note.classList.add('is-warn');
    }

    isDesktop() {
      return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    }

    showShareLinks() {
      const box = this.querySelector('[data-vc-share-links]');
      if (box) {
        box.hidden = false;
        /* Computers share in one click through a pop-up, so "Copy link" is not needed there. */
        const copy = box.querySelector('[data-vc-share-copy]');
        if (copy) copy.hidden = this.isDesktop();
        const first = box.querySelector('a, button');
        if (first) first.focus({ preventScroll: true });
      }
    }

    /* Share links (desktop / no share sheet): the revive only counts once the player has actually
       gone to the other tab or app for at least 4 seconds and comes back. */
    onShareLink(link, e) {
      if (!this.revived) this.saveResume(true);
      /* Computers: open the share page in a small pop-up window. Closing it after at least
         4 seconds revives right away; no need to switch back to the game tab. */
      if (this.isDesktop() && link.href) {
        const w = 600;
        const h = 640;
        const left = Math.max(0, (window.screenX || 0) + ((window.outerWidth || w) - w) / 2);
        const top = Math.max(0, (window.screenY || 0) + ((window.outerHeight || h) - h) / 2);
        const popup = window.open(link.href, 'vc-share', `width=${w},height=${h},left=${left},top=${top}`);
        if (popup) {
          if (e) e.preventDefault();
          if (this.popupWatch) clearInterval(this.popupWatch);
          const opened = performance.now();
          this.popupWatch = setInterval(() => {
            if (!popup.closed) return;
            clearInterval(this.popupWatch);
            this.popupWatch = null;
            if (performance.now() - opened >= 4000) this.revive();
            else this.shareNotDone();
          }, 400);
          return;
        }
        /* Pop-ups blocked: the link opens in a new tab as before. */
      }
      if (link.hasAttribute('data-vc-share-copy')) {
        const url = this.dataset.shareUrl || location.href;
        const after = () => {
          if (!link.dataset.orig) link.dataset.orig = link.textContent;
          link.textContent = link.dataset.copied || 'Link copied!';
          const note = this.querySelector('.vc-revive__note');
          if (note) {
            if (!note.dataset.orig) note.dataset.orig = note.textContent;
            note.textContent = this.dataset.pasteHint || 'Paste the link to a friend, then come back here to revive.';
            note.classList.remove('is-warn');
          }
        };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(after, after);
        else this.copyFallback(url, after);
      }
      this.watchAway(4000);
    }

    /* The share counts once the player has been away for minMs: another tab or app (phones), or
       another window such as a chat app where they pasted the link (computers). */
    watchAway(minMs) {
      const stop = () => {
        if (!this.awayWatch) return;
        document.removeEventListener('visibilitychange', this.awayWatch);
        window.removeEventListener('blur', this.awayWatch);
        window.removeEventListener('focus', this.awayWatch);
        this.awayWatch = null;
      };
      stop();
      let leftAt = 0;
      this.awayWatch = (e) => {
        const gone = e && e.type === 'blur' ? true : e && e.type === 'focus' ? false : document.hidden;
        if (gone) {
          if (!leftAt) leftAt = performance.now();
          return;
        }
        if (!leftAt) return;
        if (performance.now() - leftAt >= minMs) {
          stop();
          this.revive();
        } else {
          leftAt = 0;
          this.shareNotDone();
        }
      };
      document.addEventListener('visibilitychange', this.awayWatch);
      window.addEventListener('blur', this.awayWatch);
      window.addEventListener('focus', this.awayWatch);
    }

    strictCover() {
      return !!(this.pile && this.view3d && this.level === LEVELS.length - 1);
    }

    /* Level timer (level 3): counts down only while playing; pausing or leaving the tab stops it. */
    tickTimer() {
      const now = performance.now();
      const dt = this.lastTick ? now - this.lastTick : 0;
      this.lastTick = now;
      if (!this.timed || !this.running || document.hidden || this.timeLeft <= 0) return;
      this.timeLeft = Math.max(0, this.timeLeft - dt);
      this.renderTimer();
      if (this.timeLeft <= 0) {
        this.running = false;
        this.loseReason = 'time';
        this.updateHud();
        this.track('level_fail', { reason: 'time' });
        Sfx.play('lose');
        this.show('lose');
        this.saveResume(false);
      }
    }

    renderTimer() {
      const el = this.querySelector('[data-vc-timer]');
      if (!el) return;
      el.hidden = !this.timed;
      if (!this.timed) return;
      const sec = Math.ceil(this.timeLeft / 1000);
      const text = `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
      const out = el.querySelector('[data-vc-time]');
      if (out && out.textContent !== text) out.textContent = text;
      el.classList.toggle('is-urgent', sec <= 15);
    }

    revive() {
      if (this.revived || this.running) return;
      this.clearResume();
      this.revived = true;
      this.track('revive');
      Sfx.play('revive');
      const timeUp = this.loseReason === 'time';
      if (timeUp) this.timeLeft = 60000;
      this.loseReason = '';
      /* Out of time: +60 seconds (and 3 toys back only if the tray is also full). Tray full: 3 toys back. */
      const back = timeUp && this.tray.length < SLOTS ? [] : this.tray.slice(-3);
      back.forEach((item) => {
        item.state = 'board';
        item.landing = false;
      });
      const ids = new Set(back.map((i) => i.id));
      this.tray = this.tray.filter((s) => !ids.has(s.id));
      this.history = this.history.filter((s) => !ids.has(s.id));
      const box = this.querySelector('[data-vc-share-links]');
      if (box) box.hidden = true;
      this.renderBoard();
      this.renderSlots();
      this.running = true;
      this.updateHud();
      this.show(null);
      this.renderTimer();
      this.toast(timeUp ? this.dataset.timeToast || 'Revived! +60 seconds.' : this.dataset.reviveToast || 'Revived! 3 toys are back in the box.');
    }

    useTool(name) {
      if (!this.running || this.busy || this.inFlight || this.clearing || this.toolCounts[name] <= 0) return;
      let used = false;
      if (name === 'undo') used = this.undo();
      if (name === 'shuffle') used = this.reshuffle();
      if (name === 'hint') used = this.hint();
      if (used) {
        this.toolCounts[name] -= 1;
        Sfx.play('tool');
      }
      this.updateHud();
    }

    undo() {
      const item = this.history.pop();
      if (!item) return false;
      this.tray = this.tray.filter((s) => s.id !== item.id);
      item.state = 'board';
      this.renderBoard();
      this.renderSlots();
      return true;
    }

    reshuffle() {
      const onBoard = this.items.filter((i) => i.state === 'board');
      if (onBoard.length < 2) return false;
      if (this.pile) {
        shuffle(onBoard).forEach((item, i) => {
          Object.assign(item, this.pileSpot());
          item.z = i;
        });
        this.renderBoard();
        return true;
      }
      const types = shuffle(onBoard.map((i) => i.type));
      onBoard.forEach((item, i) => (item.type = types[i]));
      this.renderBoard();
      return true;
    }

    hint() {
      const open = this.items.filter((i) => i.state === 'board' && !this.isCovered(i));
      if (!open.length) return false;
      const countIn = (t) => this.tray.filter((s) => s.type === t).length;
      const countOpen = (t) => open.filter((o) => o.type === t).length;
      open.sort((a, b) => countIn(b.type) * 3 + countOpen(b.type) - (countIn(a.type) * 3 + countOpen(a.type)));
      const best = open[0];
      open
        .filter((o) => o.type === best.type)
        .slice(0, Math.max(1, 3 - countIn(best.type)))
        .forEach((p) => {
          if (this.onHint) this.onHint(p);
          const el = this.itemEls[p.id];
          if (!el) return;
          el.classList.add('is-hint');
          setTimeout(() => el.classList.remove('is-hint'), 1800);
        });
      return true;
    }
  }

  customElements.define('vibe-catch-game', VibeCatchGame);
})();
